import type { Asset } from 'contentful'
import ImageBase, { type ImageBaseProps } from '@components/image-base'
import { contentfulImageLoader } from '@lib/contentful-image-loader'
import { ensureProtocol } from '@lib/utilities/page-utilities'
import {
  aspectRatioStringToNumber,
  buildContentfulImageParams,
  buildContentfulImageUrl,
  getNativeAspectRatio,
  type BreakpointsAtDimension,
} from '@lib/services/image-service'
import { type ContentfulImagesAPI } from '@lib/types'
import type { ImageLoaderProps } from 'next/image'

export type ImageContentfulProps = {
  /** Contentful Asset object representing the image */
  image: Asset
  /** Contentful aspect ratio override (e.g., "16:9") - generally comes from Contentful  */
  aspectRatioOverride?: string
  /** Contentful focal point parameter for image cropping - generally comes from Contentful */
  focalPoint?: ContentfulImagesAPI['f']
  /** Alt text for the image */
  alt?: string
  /** Responsive heights for each breakpoint (height-driven srcset/sizes). Only one of breakpointWidths or breakpointHeights may be used per instance. */
  breakpointHeights?: BreakpointsAtDimension
  /** Responsive widths for each breakpoint (width-driven srcset/sizes). Only one of breakpointWidths or breakpointHeights may be used per instance. */
  breakpointWidths?: BreakpointsAtDimension
  /**
   * If true, image is considered above-the-fold (LCP candidate).
   * This is the main prop to use for above-the-fold/LCP images. It will default loading to 'eager', fetchPriority to 'high', and priority to true.
   * For below-the-fold images, leave unset or false.
   */
  isAboveTheFold?: boolean
} & Omit<ImageBaseProps, 'src' | 'alt' | 'loading' | 'fetchPriority' | 'priority'>

/**
 * Renders an image from a Contentful Asset, supporting responsive breakpoints, aspect ratio overrides,
 * and Contentful-specific image parameters. Uses ImageBase for rendering and can provide a custom loader.
 *
 * Sizing strategy notes:
 * - If you use `breakpointWidths` or `breakpointHeights`, the image will have explicit width/height and does NOT require a parent aspect-ratio wrapper.
 * - If you use `columnsMax` (default fill mode), you MUST wrap the image in a parent with `relative` positioning and an aspect-ratio class (e.g., `aspect-video`). This ensures the image is sized correctly by Next.js fill mode.
 *
 * @param {ImageContentfulProps} props - The props for the Contentful image component.
 * @returns {JSX.Element | null} The rendered image element or null.
 */
const ImageContentful = ({
  image,
  columnsMax,
  breakpointWidths,
  breakpointHeights,
  aspectRatioOverride,
  focalPoint,
  alt,
  isAboveTheFold,
  ...rest
}: ImageContentfulProps): JSX.Element | null => {
  // Ensure file URL exists
  const fileUrl = image.fields.file?.url
  if (!fileUrl) return null

  // Only allow one of breakpointWidths or breakpointHeights
  if (breakpointWidths && breakpointHeights) {
    throw new Error(
      'ImageContentful: Only one of breakpointWidths or breakpointHeights may be provided.'
    )
  }

  // If no breakpoints are provided, use the default loader (Next.js loader)
  const useDefaultLoader = !breakpointWidths && !breakpointHeights

  // We need a pure src without args if we are using the default loader
  const effectiveSrc = useDefaultLoader
    ? ensureProtocol(fileUrl)
    : buildContentfulImageUrl(ensureProtocol(fileUrl), focalPoint)

  // Use passed alt or default to Contentful title
  const effectiveAlt = alt ?? image.fields.title ?? ''

  // Use provided aspectRatioOverride, or extract it from the Contentful image Asset
  const effectiveAspectRatio =
    aspectRatioStringToNumber(aspectRatioOverride) ?? getNativeAspectRatio(image)

  // Fallback to 3 columns if no breakpoints or columnsMax are provided
  const effectiveColsMax = columnsMax ?? (useDefaultLoader ? 3 : undefined)

  // Use custom loader only when using the default loader (no breakpoints)
  const effectiveLoader = useDefaultLoader
    ? (props: ImageLoaderProps) =>
        contentfulImageLoader(props, buildContentfulImageParams(focalPoint))
    : undefined

  return (
    <ImageBase
      src={effectiveSrc}
      alt={effectiveAlt}
      aspectRatio={effectiveAspectRatio}
      columnsMax={effectiveColsMax}
      breakpointWidths={breakpointWidths}
      breakpointHeights={breakpointHeights}
      isAboveTheFold={isAboveTheFold}
      {...(effectiveLoader && { loader: effectiveLoader })}
      {...rest}
    />
  )
}

export default ImageContentful
